/**
 * Chart Theme Configuration
 *
 * Provides theme-aware colors for all chart components.
 * Uses neutral grays for structural elements to let data colors stand out.
 *
 * Usage:
 *   import { useTheme } from '../../context/ThemeContext';
 *   import { getChartTheme } from '../utils/chartTheme';
 *
 *   const { isDark } = useTheme();
 *   const theme = getChartTheme(isDark);
 */

/**
 * Get chart theme colors based on current mode
 * @param {boolean} isDark - Whether dark mode is active
 * @returns {Object} Theme color configuration
 */
export const getChartTheme = (isDark) => ({
  // Grid lines
  gridColor: isDark ? '#333' : '#e5e7eb',

  // Axes
  axisStroke: isDark ? '#666' : '#9ca3af',
  axisLabelFill: isDark ? '#888' : '#6b7280',
  tickFill: isDark ? '#999' : '#4b5563',

  // Tooltip
  tooltipBg: isDark ? '#222' : '#ffffff',
  tooltipBorder: isDark ? '#444' : '#e5e7eb',
  tooltipText: isDark ? '#eee' : '#1f2937',
  tooltipSecondaryText: isDark ? '#9ca3af' : '#6b7280',

  // Borders & containers
  borderColor: isDark ? '#5D625F' : '#d1d5db',
  borderSecondary: isDark ? '#3a3c3b' : '#e5e7eb',

  // Text
  titleColor: isDark ? '#5D625F' : '#374151',
  secondaryText: isDark ? '#9ca3af' : '#6b7280',
  noDataText: isDark ? '#9ca3af' : '#6b7280',

  // Labels on bars/charts
  barLabelFill: isDark ? '#fff' : '#1f2937',

  // Legend text
  legendText: isDark ? '#9ca3af' : '#4b5563',

  // Special elements
  heatmapLowColor: isDark ? '#1a472a' : '#dcfce7',
  heatmapHighColor: isDark ? '#4ade80' : '#16a34a',

  // Widget/Container backgrounds
  widgetBg: isDark ? '#1a1a1a' : '#ffffff',
  widgetControlBg: isDark ? 'rgba(40, 42, 41, 0.9)' : 'rgba(229, 231, 235, 0.9)',
  widgetControlHoverBg: isDark ? '#3a3c3b' : '#d1d5db',
  stickyHeaderBg: isDark ? '#1a1a1a' : '#ffffff',

  // Row backgrounds for tables
  tableRowHover: isDark ? '#252725' : '#f9fafb',
  tableRowBorder: isDark ? '#2a2c2b' : '#e5e7eb',

  // Filter/control buttons (unselected state)
  controlBg: isDark ? '#282A29' : '#f3f4f6',
  controlBorder: isDark ? '#3a3c3b' : '#d1d5db',
  controlText: isDark ? '#9ca3af' : '#6b7280',

  // Secondary cards/containers
  cardBg: isDark ? '#222321' : '#f9fafb',
  cardBorder: isDark ? '#3a3c3b' : '#e5e7eb',
});

export default getChartTheme;
